import React, { useState } from 'react';
import { MessageSquare, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { createInquiry } from '../../services/inquiryService';
import { LatteButton } from './index';

const FloatingInquiryButton = () => {
  const { user, profile } = useAuth();
  const { showSuccess, showError } = useToast();
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  
  const displayName = profile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '사용자';

  // 문의하기 제출 처리
  const handleInquirySubmit = async () => {
    if (!inquiryMessage.trim()) return;
    
    setIsSubmittingInquiry(true);
    try {
      // Supabase에 문의사항 저장
      const result = await createInquiry({
        message: inquiryMessage,
        userId: user?.id,
        userEmail: user?.email || '',
        userName: displayName
      });
      
      if (result.success) {
        // 성공 후 처리
        showSuccess('의견 감사합니다! 🙏 더 나은 서비스를 만들기 위해 노력하겠습니다.');
        setShowInquiryModal(false);
        setInquiryMessage('');
      } else {
        throw new Error(result.error?.message || '문의 접수 실패');
      }
    } catch (error) {
      console.error('문의 접수 실패:', error);
      showError('앗, 전송 중에 문제가 생겼어요. 😥 잠시 후에 다시 시도해주세요.');
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  return (
    <>
      {/* 플로팅 버튼 */}
      <button
        onClick={() => setShowInquiryModal(true)}
        className="fixed bottom-6 right-6 w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 flex items-center justify-center z-40 group"
        style={{
          background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
        }}
        title="문의하기"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute right-full mr-3 bg-gray-800 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
          💬 의견 남기기
        </span>
      </button>

      {/* 문의하기 모달 */}
      {showInquiryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md animate-in slide-in-from-bottom duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                💬 의견 남기기
              </h3>
              <button
                onClick={() => setShowInquiryModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
              </button>
            </div>
            
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              안녕하세요! 😊<br/>
              라떼이즈닷컴을 사용하시면서 불편했던 점이나 개선했으면 하는 점이 있다면 편하게 말씀해주세요!
              그 외 어떤 문의도 환영합니다. 
            </p>
            
            <textarea
              value={inquiryMessage}
              onChange={(e) => setInquiryMessage(e.target.value)}
              placeholder="예: 이런 기능이 있으면 좋겠어요 / 여기가 조금 불편해요 / 이 부분이 정말 좋아요!"
              className="w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ 
                borderColor: 'var(--color-border-primary)',
                color: 'var(--color-text-primary)'
              }}
              rows={5}
              maxLength={500}
              autoFocus
            />
            
            <div className="text-right text-xs mb-3" style={{ color: 'var(--color-text-tertiary)' }}>
              {inquiryMessage.length}/500
            </div>
            
            <div className="flex justify-end gap-2">
              <LatteButton
                onClick={() => setShowInquiryModal(false)}
                variant="ghost"
                size="sm"
              >
                취소
              </LatteButton>
              <LatteButton
                onClick={handleInquirySubmit}
                variant="primary"
                size="sm"
                disabled={!inquiryMessage.trim() || isSubmittingInquiry}
              >
                {isSubmittingInquiry ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    전송 중...
                  </>
                ) : (
                  '전송'
                )}
              </LatteButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingInquiryButton;
